import { Box, Fade, Zoom } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KIOSK__SELECTION_EMPTY_NO_EMPLOYEES,
  KIOSK__SELECTION_EMPTY_NO_MATCH,
  KIOSK__SELECTION_MATCH_COUNT,
  KIOSK__SELECTION_SECTION_ACTIVE,
  KIOSK__SELECTION_SECTION_OTHER,
  KIOSK__SELECTION_SECTION_PLANNED,
  KIOSK__CLOCK_IN_SUCCESS,
  KIOSK__EMPLOYEE_UNAVAILABLE,
  KIOSK__CLOCK_OUT_SUCCESS,
  KIOSK__DISCARD_SUCCESS,
} from 'translations/constants';
import UPSnackBar from 'components/app-container/snackbar';
import { useAppSelector } from 'store';
import { kioskSessionSelector } from 'store/selectors/kiosk-session-selectors';
import { formatTime } from 'utils/date-utils';
import kioskBackground from '../../assets/icons/KioskBackgroundWeb.svg';
import KioskDeactivateModal from './KioskDeactivateModal';
import KioskEndRegistrationView from './KioskEndRegistrationView';
import KioskPinView from './KioskPinView';
import KioskStartRegistrationView from './KioskStartRegistrationView';
import { FEED_STAGE, KIOSK_STAGE_FADE_MS, KIOSK_STAGE_ZOOM_MS, KioskStage, stageKey } from './kiosk-stage';
import { countFeed, filterFeed, isPinGatedSession } from './kiosk-selection-helpers';
import { useKioskSelectionFeed } from './useKioskSelectionFeed';
import KioskEmployeeCard from './components/KioskEmployeeCard';
import KioskEmptyState from './components/KioskEmptyState';
import KioskErrorState from './components/KioskErrorState';
import KioskGreeting from './components/KioskGreeting';
import KioskHeaderBar from './components/KioskHeaderBar';
import KioskNoPinNotice from './components/KioskNoPinNotice';
import KioskSearchField from './components/KioskSearchField';
import KioskSectionHeader from './components/KioskSectionHeader';
import {
  KioskBackgroundImage,
  KioskCardsGrid,
  KioskMatchCount,
  KioskSelectionContainer,
  KioskSelectionInner,
  KioskSessionName,
  KioskStageWrap,
} from './styles';

/**
 * Kiosk employee-selection screen (US 7346). One continuous full-screen flow: the feed, PIN pad,
 * and clock-in/out forms are stages on the same gradient, choreographed by the stage machine below.
 */
const KioskSelectionScreen = () => {
  const [t] = useTranslation();
  const session = useAppSelector(kioskSessionSelector);
  const { feed, error, reload } = useKioskSelectionFeed();
  const [query, setQuery] = useState('');
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const filtered = useMemo(() => (feed ? filterFeed(feed, query) : null), [feed, query]);
  const matchCount = filtered ? countFeed(filtered) : 0;
  const isSearching = query.trim().length > 0;

  const pinGated = isPinGatedSession(session);

  const [successName, setSuccessName] = useState<string | null>(null);
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  const [clockOutHours, setClockOutHours] = useState<string | null>(null);
  const [alreadyEndedKey, setAlreadyEndedKey] = useState<string | null>(null);
  const [discardedOpen, setDiscardedOpen] = useState(false);
  const [noPinNoticeOpen, setNoPinNoticeOpen] = useState(false);
  // Whether the currently mounted stage has a PIN verify / form submit in flight — disables the
  // persistent header's close button while true. Reported up by whichever stage is mounted.
  const [stageBusy, setStageBusy] = useState(false);

  const [target, setTarget] = useState<KioskStage>(FEED_STAGE); // where we are going
  const [mounted, setMounted] = useState<KioskStage>(FEED_STAGE); // what is currently rendered
  const entered = mounted === target; // reference equality is intentional

  const goTo = (next: KioskStage) => setTarget(next);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleExited = () => {
    containerRef.current?.scrollTo({ top: 0 }); // each view starts scrolled to the top
    setStageBusy(false); // the stage about to mount always starts idle
    setMounted(target);
  };

  // Routes a card tap through the PIN gate when the session requires it; unchanged (no token) otherwise.
  const handleCardTap = (
    intent: 'start' | 'end',
    employeeId: number,
    fullName: string,
    hasPin: boolean,
    lockedContractDateId: number | null,
  ) => {
    if (!pinGated) {
      if (intent === 'start') {
        goTo({ view: 'clock-in', employeeId, fullName, lockedContractDateId, pinToken: null });
      } else {
        goTo({ view: 'clock-out', employeeId, fullName, pinToken: null });
      }
      return;
    }
    if (!hasPin) {
      setNoPinNoticeOpen(true);
      return;
    }
    goTo({ view: 'pin', employeeId, fullName, intent, lockedContractDateId, expired: false });
  };

  const handlePinVerified = (pinVerificationToken: string) => {
    if (mounted.view !== 'pin') return;
    const { employeeId, fullName, intent, lockedContractDateId } = mounted;
    if (intent === 'start') {
      goTo({ view: 'clock-in', employeeId, fullName, lockedContractDateId, pinToken: pinVerificationToken });
    } else {
      goTo({ view: 'clock-out', employeeId, fullName, pinToken: pinVerificationToken });
    }
  };

  const handleStartPinExpired = () => {
    if (mounted.view !== 'clock-in') return;
    const { employeeId, fullName, lockedContractDateId } = mounted;
    goTo({ view: 'pin', employeeId, fullName, intent: 'start', lockedContractDateId, expired: true });
  };

  const handleEndPinExpired = () => {
    if (mounted.view !== 'clock-out') return;
    const { employeeId, fullName } = mounted;
    goTo({ view: 'pin', employeeId, fullName, intent: 'end', lockedContractDateId: null, expired: true });
  };

  // The cached hasPin was stale (deleted elsewhere) — VerifyPin is authoritative here, not the feed.
  const handleNoPinFromVerify = () => {
    goTo(FEED_STAGE);
    setNoPinNoticeOpen(true);
  };

  const handleClockInSuccess = (name: string) => {
    goTo(FEED_STAGE);
    setSuccessName(name);
    reload(); // employee moves into the Active section
  };

  const handleClockOutSuccess = (workedLabel: string) => {
    goTo(FEED_STAGE);
    setClockOutHours(workedLabel);
    reload(); // employee leaves the Active section
  };

  const handleDiscarded = () => {
    goTo(FEED_STAGE);
    setDiscardedOpen(true);
    reload();
  };

  const handleAlreadyEnded = (messageKey: string) => {
    goTo(FEED_STAGE);
    setAlreadyEndedKey(messageKey);
    reload();
  };

  // 204 on load: the registration was ended elsewhere → send this employee to clock-in instead.
  // The token (if any) is bound to (session, employee) — still valid for the clock-in call too.
  const handleNoActiveRegistration = () => {
    if (mounted.view !== 'clock-out') return;
    const { employeeId, fullName, pinToken } = mounted;
    goTo({ view: 'clock-in', employeeId, fullName, lockedContractDateId: null, pinToken });
  };

  const handleEmployeeUnavailable = () => {
    goTo(FEED_STAGE);
    setUnavailableOpen(true);
    reload();
  };

  const onFeed = target.view === 'feed'; // drives header state instantly, ahead of the stage animation

  return (
    <KioskSelectionContainer ref={containerRef} scrollable={mounted.view !== 'pin'}>
      <KioskBackgroundImage src={kioskBackground} alt="" aria-hidden visible={target.view !== 'feed'} />

      <KioskHeaderBar
        onReload={reload}
        reloadVisible={onFeed}
        variant={onFeed ? 'logout' : 'close'}
        onAction={onFeed ? () => setDeactivateOpen(true) : () => goTo(FEED_STAGE)}
        actionDisabled={!onFeed && stageBusy}
      />

      {mounted.view === 'feed' && (
        <Fade in={entered} appear timeout={KIOSK_STAGE_FADE_MS} onExited={handleExited}>
          <KioskSelectionInner key="feed">
            <KioskGreeting />
            {session?.name && <KioskSessionName>{session.name}</KioskSessionName>}
            <KioskSearchField value={query} onChange={setQuery} />

            {isSearching && filtered && !error && (
              <KioskMatchCount>
                {t(KIOSK__SELECTION_MATCH_COUNT, { count: matchCount, query: query.trim() })}
              </KioskMatchCount>
            )}

            {error && <KioskErrorState />}

            {!error && filtered && matchCount === 0 && (
              <KioskEmptyState
                messageKey={isSearching ? KIOSK__SELECTION_EMPTY_NO_MATCH : KIOSK__SELECTION_EMPTY_NO_EMPLOYEES}
              />
            )}

            {!error && filtered && matchCount > 0 && (
              <>
                {filtered.active.length > 0 && (
                  <>
                    <KioskSectionHeader titleKey={KIOSK__SELECTION_SECTION_ACTIVE} />
                    <KioskCardsGrid>
                      {filtered.active.map(e => (
                        <KioskEmployeeCard
                          key={`active-${e.employeeId}`}
                          variant="active"
                          fullName={e.fullName}
                          startTimeUtc={e.startTimeUtc}
                          timeLabel={formatTime(e.startTimeUtc)}
                          onSelect={() => handleCardTap('end', e.employeeId, e.fullName, e.hasPin, null)}
                        />
                      ))}
                    </KioskCardsGrid>
                  </>
                )}

                {filtered.planned.length > 0 && (
                  <>
                    <KioskSectionHeader titleKey={KIOSK__SELECTION_SECTION_PLANNED} />
                    <KioskCardsGrid>
                      {filtered.planned.map(e => (
                        <KioskEmployeeCard
                          key={`planned-${e.employeeId}`}
                          variant="planned"
                          fullName={e.fullName}
                          timeLabel={formatTime(e.plannedStartUtc)}
                          onSelect={() => handleCardTap('start', e.employeeId, e.fullName, e.hasPin, e.contractDateId)}
                        />
                      ))}
                    </KioskCardsGrid>
                  </>
                )}

                {filtered.allEmployees.length > 0 && (
                  <>
                    <KioskSectionHeader titleKey={KIOSK__SELECTION_SECTION_OTHER} />
                    <KioskCardsGrid>
                      {filtered.allEmployees.map(e => (
                        <KioskEmployeeCard
                          key={`other-${e.employeeId}`}
                          variant="other"
                          fullName={e.fullName}
                          onSelect={() => handleCardTap('start', e.employeeId, e.fullName, e.hasPin, null)}
                        />
                      ))}
                    </KioskCardsGrid>
                  </>
                )}
              </>
            )}
          </KioskSelectionInner>
        </Fade>
      )}

      {mounted.view === 'pin' && (
        // Fade wraps a plain host <div> (not Zoom directly) so its ref/style land on a genuinely
        // separate DOM node from the one Zoom controls — MUI's Fade/Zoom both set the `transition`
        // CSS property imperatively on their own single child, so nesting them onto the SAME node
        // (which happens if Fade's child is the Zoom component itself) makes one clobber the other's
        // transition, leaving only one of opacity/transform actually animated.
        <Fade in={entered} appear timeout={KIOSK_STAGE_ZOOM_MS} onExited={handleExited}>
          <Box height="100%">
            <Zoom in={entered} appear timeout={KIOSK_STAGE_ZOOM_MS}>
              <KioskStageWrap key={stageKey(mounted)}>
                <KioskPinView
                  employeeId={mounted.employeeId}
                  employeeName={mounted.fullName}
                  expired={mounted.expired}
                  onClose={() => goTo(FEED_STAGE)}
                  onVerified={handlePinVerified}
                  onNoPin={handleNoPinFromVerify}
                  onBusyChange={setStageBusy}
                />
              </KioskStageWrap>
            </Zoom>
          </Box>
        </Fade>
      )}

      {mounted.view === 'clock-in' && (
        <Fade in={entered} appear timeout={KIOSK_STAGE_FADE_MS} onExited={handleExited}>
          <KioskStageWrap key={stageKey(mounted)}>
            <KioskStartRegistrationView
              employeeId={mounted.employeeId}
              employeeName={mounted.fullName}
              lockedContractDateId={mounted.lockedContractDateId}
              pinVerificationToken={mounted.pinToken}
              onSuccess={handleClockInSuccess}
              onEmployeeUnavailable={handleEmployeeUnavailable}
              onPinVerificationExpired={handleStartPinExpired}
              onBusyChange={setStageBusy}
            />
          </KioskStageWrap>
        </Fade>
      )}

      {mounted.view === 'clock-out' && (
        <Fade in={entered} appear timeout={KIOSK_STAGE_FADE_MS} onExited={handleExited}>
          <KioskStageWrap key={stageKey(mounted)}>
            <KioskEndRegistrationView
              employeeId={mounted.employeeId}
              employeeName={mounted.fullName}
              pinVerificationToken={mounted.pinToken}
              onSuccess={handleClockOutSuccess}
              onDiscarded={handleDiscarded}
              onEmployeeUnavailable={handleEmployeeUnavailable}
              onAlreadyEnded={handleAlreadyEnded}
              onNoActiveRegistration={handleNoActiveRegistration}
              onPinVerificationExpired={handleEndPinExpired}
              onBusyChange={setStageBusy}
            />
          </KioskStageWrap>
        </Fade>
      )}

      <KioskDeactivateModal open={deactivateOpen} onClose={() => setDeactivateOpen(false)} />

      <KioskNoPinNotice open={noPinNoticeOpen} onClose={() => setNoPinNoticeOpen(false)} />

      <UPSnackBar
        open={Boolean(successName)}
        onClose={() => setSuccessName(null)}
        content={successName ? t(KIOSK__CLOCK_IN_SUCCESS, { employeeName: successName }) : ''}
        type="success"
        vertical="bottom"
        horizontal="right"
        index={0}
        hideDuration={4000}
      />

      <UPSnackBar
        open={unavailableOpen}
        onClose={() => setUnavailableOpen(false)}
        content={t(KIOSK__EMPLOYEE_UNAVAILABLE)}
        type="warning"
        vertical="bottom"
        horizontal="right"
        index={1}
        hideDuration={4000}
      />

      <UPSnackBar
        open={Boolean(clockOutHours)}
        onClose={() => setClockOutHours(null)}
        content={clockOutHours ? t(KIOSK__CLOCK_OUT_SUCCESS, { workedHours: clockOutHours }) : ''}
        type="success"
        vertical="bottom"
        horizontal="right"
        index={2}
        hideDuration={4000}
      />

      <UPSnackBar
        open={Boolean(alreadyEndedKey)}
        onClose={() => setAlreadyEndedKey(null)}
        content={alreadyEndedKey ? t(alreadyEndedKey) : ''}
        type="warning"
        vertical="bottom"
        horizontal="right"
        index={3}
        hideDuration={4000}
      />

      <UPSnackBar
        open={discardedOpen}
        onClose={() => setDiscardedOpen(false)}
        content={t(KIOSK__DISCARD_SUCCESS)}
        type="success"
        vertical="bottom"
        horizontal="right"
        index={4}
        hideDuration={4000}
        testId="kiosk-discard-success-banner"
      />
    </KioskSelectionContainer>
  );
};

export default KioskSelectionScreen;
